import { isBoolean, isInteger, isObject, isString } from "lodash-es";
import { defineStore } from "pinia";

import { userSettingChanged } from "@/analytics/events/other";
import { trackEvent, trackUserProperty } from "@/analytics/track";
import { sendSetting } from "@/composables/useEventBus";
import type { WorkUnit } from "@/model/analytics";
import type { WorkingMode } from "@/model/baseTypes";
import { assureBoardType } from "@/model/board";
import type { Session } from "@/model/session";
import type { StickyType } from "@/model/stickyType";
import { storesLen } from "@/storesPlugin";
import { LocalStore } from "@/utils/LocalStore";
import { keysIn } from "@/utils/type";

import { useBoardStore } from "./board";
import { useSessionStore } from "./session";
import { statsProvider } from "./statistics";

export const fonts = ["Manrope", "Caveat", "Natural", "Child", "Lefthand", "Bedtime"] as const;

function isFont(name: unknown): name is (typeof fonts)[number] {
  return isString(name) && fonts.includes(name);
}

export type StickyNoteTextAlignment = "left" | "center" | "right";
type InputDevice = "mouse" | "trackpad" | "auto";
type EffectiveInputDevice = Exclude<InputDevice, "auto">;

function isInputDevice(value: unknown): value is InputDevice {
  return isString(value) && ["mouse", "trackpad", "auto"].includes(value);
}

function isEffectiveInputDevice(value: unknown): value is EffectiveInputDevice {
  return isString(value) && ["mouse", "trackpad"].includes(value);
}

export const textAlignmentOptions: Array<{ value: StickyNoteTextAlignment; label: string }> = [
  { value: "left", label: /*$t*/ "settings.stickyNoteTextAlignment.left" },
  { value: "center", label: /*$t*/ "settings.stickyNoteTextAlignment.center" },
  { value: "right", label: /*$t*/ "settings.stickyNoteTextAlignment.right" },
];

function isAlignment(value: unknown): value is StickyNoteTextAlignment {
  return textAlignmentOptions.some((option) => option.value === value);
}

interface ChartDateRange {
  start?: string | null;
  end?: string | null;
}

interface ChartPreferences {
  workUnit?: WorkUnit;
  hideNonWorkingDays?: boolean;
  dateRanges?: Record<string, ChartDateRange>;
}

export interface ClientSettings {
  workingModes: Record<Session["id"], WorkingMode>;
  stickyFont: (typeof fonts)[number];
  inputDevice: InputDevice; // replaces wheelZoom
  effectiveInputDevice: EffectiveInputDevice;
  overload: boolean;
  permanentTeamRiskCanvasLinks: boolean;
  permanentProgramLinks: boolean;
  highlightDoneLinks: boolean;
  initialLineupDistance: number;
  stackDistanceX: number;
  stackDistanceY: number;
  showStats: number;
  overviewScale: number;
  defaultStickyTypes: Record<string, string>;
  highContrastMode: boolean;
  panelWidths: Record<string, number>;
  textAlignment: StickyNoteTextAlignment;
  archivedSessions: boolean;
  chartPreferences: Record<string, ChartPreferences>;
}

type Setting = keyof ClientSettings;

export interface ClientSettingsStoreData extends ClientSettings {
  effectiveInputSwitchCount: number;
}

export const useClientSettingsStore = defineStore("clientSettings", {
  state: () => loadSettings(),
  getters: {
    defaultStickyTypeId(state): string {
      return state.defaultStickyTypes[this.defaultStickyTypeKey];
    },
    defaultStickyTypeKey: () => {
      const board = useBoardStore().currentBoard();
      return (
        useSessionStore().current.id +
        "-" +
        board.type +
        "-" +
        (assureBoardType(board, "flex")?.flexType.id || "")
      );
    },
    panelWidth(state) {
      return (name: string) => state.panelWidths[name];
    },
    chartPreference(state) {
      return (chartId: string): ChartPreferences => state.chartPreferences[chartId] ?? {};
    },
    workingMode(state) {
      const sessionId = useSessionStore().current.id;
      const defaultWorkingMode = "planning";
      return state.workingModes[sessionId] || defaultWorkingMode;
    },
  },
  actions: {
    init() {
      this.$subscribe((_, state) => {
        saveSettings(state);
      });
    },
    toggleShowStats(level: number) {
      this.showStats = this.showStats !== level ? level : 0;
      setStoresLenStatsProvider(this.showStats);
    },
    toggleArchivedSessions() {
      this.archivedSessions = !this.archivedSessions;
    },
    setDefaultStickyType(type?: StickyType) {
      trackEvent(userSettingChanged({ key: "defaultStickyType", toValue: type?.functionality }));
      if (type) {
        this.defaultStickyTypes[this.defaultStickyTypeKey] = type?.id;
      } else {
        delete this.defaultStickyTypes[this.defaultStickyTypeKey];
      }
    },
    setPanelWidth(name: string, width: number) {
      this.panelWidths[name] = width;
    },
    setChartPreference(chartId: string, prefs: Partial<ChartPreferences>) {
      this.chartPreferences[chartId] = { ...this.chartPreference(chartId), ...prefs };
    },
    setWorkingMode(mode: WorkingMode) {
      const sessionId = useSessionStore().current.id;
      this.workingModes[sessionId] = mode;
      trackUserProperty("workingMode", mode);
    },
    setInputDevice(device: InputDevice) {
      if (this.inputDevice !== device) {
        this.inputDevice = device;
        if (device !== "auto") {
          this.setEffectiveInputDevice(device);
        }
      }
    },
    setEffectiveInputDevice(device: EffectiveInputDevice) {
      if (this.effectiveInputDevice !== device) {
        this.effectiveInputDevice = device;
      }
    },
  },
});

const settings: { [K in Setting]: LocalStore<ClientSettings[K]> } = {
  workingModes: store("workingModes", {}, isObject), // Working mode set per-session
  stickyFont: store<(typeof fonts)[number]>("stickyFont", "Manrope", isFont),
  inputDevice: store<InputDevice>("inputDevice", "auto", isInputDevice),
  effectiveInputDevice: store<EffectiveInputDevice>(
    "effectiveInputDevice",
    "mouse",
    isEffectiveInputDevice,
  ),
  overload: store("overload", true, isBoolean),
  permanentTeamRiskCanvasLinks: store("permanentTeamRiskCanvasLinks", false, isBoolean),
  permanentProgramLinks: store("permanentProgramLinks", true, isBoolean),
  highlightDoneLinks: store("highlightDoneLinks", true, isBoolean),
  initialLineupDistance: store("initialLineupDistance", 110, isInteger),
  stackDistanceX: store("stackDistanceX", 15, isInteger),
  stackDistanceY: store("stackDistanceY", 15, isInteger),
  showStats: store("showStats", 0, isInteger),
  overviewScale: store("overviewScale", 6, isInteger),
  defaultStickyTypes: store("defaultStickyTypes", {}, isObject),
  // To meet accessibility requirements
  highContrastMode: store("highContrastMode", false, isBoolean),
  panelWidths: store("panelWidths", {}, isObject),
  chartPreferences: store("chartPreferences", {}, isObject),
  textAlignment: store<StickyNoteTextAlignment>("textAlignment", "left", isAlignment),
  archivedSessions: store("archivedSessions", false, isBoolean),
};

function loadSettings(): ClientSettingsStoreData {
  // Local var to help check if our algorithm to set effectiveInputDevice is working
  const res = { effectiveInputSwitchCount: 0 } as ClientSettingsStoreData;
  for (const prop of keysIn(settings)) {
    loadSetting(res, prop);
  }
  initEffectiveInputDevice(res);
  setStoresLenStatsProvider(res.showStats);
  return res;
}

function saveSettings(data: ClientSettings) {
  for (const prop of keysIn(settings)) {
    saveSetting(data, prop);
  }
}

function loadSetting<T extends Setting>(data: ClientSettings, prop: T) {
  data[prop] = settings[prop].load()!;
}

function saveSetting<T extends Setting>(data: ClientSettings, prop: T) {
  settings[prop].save(data[prop]);
}

/**
 * Handles any mismatches between inputDevice (what the user selects)
 * and effectiveInputDevice (what gets applied).
 */
function initEffectiveInputDevice(state: ClientSettingsStoreData) {
  const device = state.inputDevice;
  if (device === "mouse" || device === "trackpad") {
    state.effectiveInputDevice = device;
  }
}
function setStoresLenStatsProvider(showStats: number) {
  statsProvider.storesLen = showStats === 0 ? () => 0 : () => storesLen(["statistics"]);
}

function store<T>(key: string, defaultVal: T, valid: (data: unknown) => boolean) {
  const store: LocalStore<T> = new LocalStore<T>(key, {
    sedes: {
      serialize: (value) => {
        sendSetting(key, value);
        return value;
      },
      deserialize: (data: unknown): T | undefined => {
        if (valid(data)) {
          return data as T;
        }
        store.save(defaultVal);
        return defaultVal;
      },
    },
  });
  return store;
}
